/**
 * Free Gift With Purchase - Cart Automation
 * For doctorbhargavad2d (Sense theme)
 *
 * v2 - Fixed for Sense theme's actual markup:
 *   Quantity wrapper: .product-form__quantity (confirmed via DOM inspection, June 2026)
 *
 * Behavior:
 * - As soon as the customer types/steps the quantity to >= threshold,
 *   the gift is added to the cart automatically (debounced ~600ms after typing stops)
 * - If they drop back below threshold, the gift is removed automatically
 *   (only removes gifts WE added, tagged via line item property)
 * - Progress bar / status text updates live on every keystroke (no debounce needed for UI)
 *
 * Install:
 * 1. Upload this file as assets/free-gift.js (replace the old version)
 * 2. Confirm in layout/theme.liquid, just above </head>:
 *    <script src="{{ 'free-gift.js' | asset_url }}" defer></script>
 */

(function () {
  console.log('[free-gift] script loaded');
  let debounceTimer = null;

  function getPromoEl() {
    return document.querySelector('.free-gift-promo');
  }

  function getQuantityInput() {
    return document.querySelector('.product-form__quantity input[type="number"]');
  }

  function updateProgressUI(currentQty, threshold, giftConfirmed) {
    const promo = getPromoEl();
    if (!promo) return;

    const fill = promo.querySelector('.free-gift-promo__progress-fill');
    const status = promo.querySelector('.free-gift-promo__status');

    const pct = Math.min(100, Math.round((currentQty / threshold) * 100));
    if (fill) fill.style.width = pct + '%';

    if (currentQty >= threshold) {
      promo.classList.add('is-unlocked');
      if (status) {
        status.textContent = giftConfirmed
          ? '✅ Free gift unlocked! Added it to your cart'
          : '🎉 Free gift unlocked! Adding it to your cart...';
      }
    } else {
      promo.classList.remove('is-unlocked');
      const remaining = threshold - currentQty;
      if (status) {
        status.innerHTML = 'Add <span class="free-gift-promo__remaining">' + remaining + '</span> more to unlock your free gift';
      }
    }
  }

  async function getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function addGift(giftVariantId, giftLabel) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: giftVariantId,
              quantity: 1,
              properties: { _free_gift: 'true', _gift_for: giftLabel }
            }
          ]
        })
      });
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    } catch (e) {
      console.error('Free gift add failed:', e);
    }
  }

  async function removeGift(giftVariantId) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: giftVariantId, quantity: 0 })
      });
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    } catch (e) {
      console.error('Free gift remove failed:', e);
    }
  }

  async function reconcileGiftForTypedQty(typedQty) {
    const promo = getPromoEl();
    if (!promo) return;

    const threshold = parseInt(promo.dataset.giftThreshold, 10);
    const giftVariantId = promo.dataset.giftVariantId;
    const giftLabel = promo.dataset.giftLabel;

    const cart = await getCart();
    let giftLine = cart.items.find((item) => String(item.variant_id) === String(giftVariantId));

    if (typedQty >= threshold && !giftLine) {
      await addGift(giftVariantId, giftLabel);
      giftLine = true; // just added, treat as confirmed
    } else if (typedQty < threshold && giftLine && giftLine.properties && giftLine.properties._free_gift === 'true') {
      await removeGift(giftVariantId);
      giftLine = false;
    }

    updateProgressUI(typedQty, threshold, !!giftLine);
  }

  function handleQuantityChange(qtyInput, threshold) {
    const val = parseInt(qtyInput.value, 10) || 1;

    updateProgressUI(val, threshold);

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      reconcileGiftForTypedQty(val);
    }, 600);
  }

  function watchQuantityInput() {
    const promo = getPromoEl();
    if (!promo) return;

    const threshold = parseInt(promo.dataset.giftThreshold, 10);
    const qtyInput = getQuantityInput();

    if (!qtyInput) {
      console.warn('Free gift: quantity input not found, progress bar will not sync live.');
      return;
    }

    updateProgressUI(parseInt(qtyInput.value, 10) || 1, threshold);

    qtyInput.addEventListener('input', () => handleQuantityChange(qtyInput, threshold));
    qtyInput.addEventListener('change', () => handleQuantityChange(qtyInput, threshold));

    const wrapper = qtyInput.closest('.product-form__quantity');
    if (wrapper) {
      wrapper.addEventListener('click', (e) => {
        if (e.target.closest('button')) {
          setTimeout(() => handleQuantityChange(qtyInput, threshold), 50);
        }
      });
    }
  }

  async function reconcileCartGiftOnLoad() {
    const promo = getPromoEl();
    if (!promo) return;

    const threshold = parseInt(promo.dataset.giftThreshold, 10);
    const giftVariantId = promo.dataset.giftVariantId;
    const mainVariantId = promo.dataset.mainVariantId;
    const giftLabel = promo.dataset.giftLabel;

    const cart = await getCart();
    const mainQtyInCart = cart.items
      .filter((item) => String(item.variant_id) === String(mainVariantId))
      .reduce((sum, item) => sum + item.quantity, 0);

    let giftLine = cart.items.find((item) => String(item.variant_id) === String(giftVariantId));

    if (mainQtyInCart >= threshold && !giftLine) {
      await addGift(giftVariantId, giftLabel);
      giftLine = true;
    } else if (mainQtyInCart < threshold && giftLine && giftLine.properties && giftLine.properties._free_gift === 'true') {
      await removeGift(giftVariantId);
      giftLine = false;
    }

    // Reflect the cart's actual qty for this variant in the UI, not just the typed box value
    const qtyInput = getQuantityInput();
    const displayQty = qtyInput ? (parseInt(qtyInput.value, 10) || mainQtyInCart) : mainQtyInCart;
    updateProgressUI(Math.max(displayQty, mainQtyInCart), threshold, !!giftLine);
  }

  function init() {
    const promo = getPromoEl();
    console.log('[free-gift] promo element found:', !!promo);
    if (promo) {
      console.log('[free-gift] threshold:', promo.dataset.giftThreshold, 'giftVariantId:', promo.dataset.giftVariantId, 'mainVariantId:', promo.dataset.mainVariantId);
    }
    console.log('[free-gift] quantity input found:', !!getQuantityInput());

    watchQuantityInput();
    reconcileCartGiftOnLoad();

    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form && form.action && form.action.includes('/cart/add')) {
        setTimeout(reconcileCartGiftOnLoad, 800);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
